<template>
  <div class="cart-page">
    <section class="cart-content">
      <div class="container">
        <div v-if="cartItems.length > 0" class="cart-wrapper">
          <!-- Cart Items -->
          <div class="cart-items-section">
            <div class="section-header">
              <h2 class="section-title">{{ t('header.cart.title') }} ({{ cartCount }})</h2>
              <button @click="handleClearCart" class="btn-clear-cart" :disabled="isSyncing">
                {{ t('header.cart.clear_cart') }}
              </button>
            </div>
            <div class="cart-items">
              <div v-for="item in cartItems" :key="`${item.id}-${item.color}-${item.size}`" class="cart-item">
                <div class="item-image">
                  <img :src="getImagePath(item.image)" :alt="item.name" class="cart-item-image" />
                </div>
                
                <div class="item-content-wrapper">
                  <div class="item-details">
                    <div class="item-header-row">
                      <h3 class="item-name">{{ item.name }}</h3>
                       <button @click="handleRemoveFromCart(item.id, item.color, item.size, item.variantId, item.cartItemId)" class="btn-remove" :title="t('account.personal_data.btn.cancel')">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>
                    <p class="item-info">
                      <span v-if="item.size">{{ item.size }}</span>
                      <span v-if="item.size && item.color" class="separator"> | </span>
                      <span v-if="item.color">{{ item.color }}</span>
                    </p>
                    <div v-if="item.categoryTypeSku || item.sku" class="item-sku-row">
                      <span class="sku-label-text">COD.</span>
                      <span class="sku-value">{{ item.categoryTypeSku || item.sku }}</span>
                    </div>
                    <div class="item-price-wrapper">
                      <span class="item-price">{{ formatPrice(item.price) }}</span>
                      <template v-if="Number(item.oldPrice) > Number(item.price)">
                        <span class="item-old-price">{{ formatPrice(item.oldPrice) }}</span>
                        <span class="item-discount-badge">-{{ Math.round(item.discount || ((1 - Number(item.price) / Number(item.oldPrice)) * 100)) }}%</span>
                      </template>
                    </div>

                    <!-- Premium Info Box integrated per item -->
                    <div class="item-premium-info mt-3">
                      <div class="item-info-box">
                        <div class="info-box-icon-small">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                          </svg>
                        </div>
                        <div class="info-box-text">
                          <span class="info-box-title-small">{{ t('header.cart.trust.returns') }}</span>
                          <span class="info-box-desc-small">{{ t('header.cart.trust.return_window') }}</span>
                        </div>
                      </div>
                      
                      <div class="item-info-box clickable" @click="showSizeGuide = true">
                        <div class="info-box-icon-small">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                            <path d="M2 17l10 5 10-5"></path>
                            <path d="M2 12l10 5 10-5"></path>
                          </svg>
                        </div>
                        <div class="info-box-text">
                          <span class="info-box-title-small">{{ t('product.size_guide.title') }}</span>
                          <span class="info-box-link-small">{{ t('product.labels.show_more') }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="item-footer-actions">
                    <div class="quantity-selector">
                       <button @click="handleUpdateQuantity(item.id, item.color, item.size, item.quantity - 1, item.variantId, item.cartItemId)" class="qty-btn">−</button>
                      <span class="qty-value">{{ item.quantity }}</span>
                       <button @click="handleUpdateQuantity(item.id, item.color, item.size, item.quantity + 1, item.variantId, item.cartItemId)" class="qty-btn">+</button>
                    </div>
                    <div class="item-total">
                      <p class="total-price">{{ formatPrice(item.price * item.quantity) }}</p>
                      <div class="price-vat-label-item">{{ t('product.labels.vat_included') }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Order Summary -->
          <div class="order-summary">
            <h2 class="summary-title">{{ t('header.cart.summary') }}</h2>
            <div class="summary-content">
              <div class="cart-summary-details">
                <div class="summary-row">
                  <span class="label">{{ t('header.cart.list_price') }}</span>
                  <span class="value">{{ formatPrice(listSubtotal) }}</span>
                </div>
                
                <div v-if="totalSavings > 0.01" class="summary-row discount-row">
                  <span class="label">{{ t('header.cart.total_discount') }}</span>
                  <span class="value">- {{ formatPrice(totalSavings) }}</span>
                </div>
              </div>
              
              <div class="summary-row subtotal-row-main">
                <span class="label">{{ t('header.cart.subtotal') }}</span>
                <span class="value">{{ formatPrice(subtotal) }}</span>
              </div>
              
              <div class="summary-row">
                <span class="label">{{ t('header.cart.shipping') }}</span>
                <div class="value-wrapper">
                  <span class="value" :class="{ 'free-shipping': shipping === 0 }">
                    {{ shipping === 0 ? t('header.cart.free').toUpperCase() : formatPrice(shipping) }}
                  </span>
                </div>
              </div>
              
              <div class="summary-divider"></div>
              
              <div class="summary-row total-row">
                <span class="label">{{ t('header.cart.total') }}</span>
                <div class="total-value-container text-end">
                  <span class="value">{{ formatPrice(total) }}</span>
                  <div class="price-vat-label-cart">{{ t('product.labels.vat_included') }}</div>
                </div>
              </div>

              <button @click="goToCheckout" class="btn-checkout" :disabled="isSyncing">
                <span v-if="isSyncing">{{ t('header.cart.syncing') || 'Sincronizzazione...' }}</span>
                <span v-else>{{ t('header.cart.checkout') }}</span>
              </button>

              <!-- Payment Trust Info -->
              <div class="payment-trust-info">
                <!-- Divider Line -->
                <div class="trust-divider"></div>

                <!-- Payment Logos -->
                <div class="payment-methods">
                  <div class="payment-methods-row">
                    <!-- Apple Pay -->
                    <div class="payment-method-card">
                      <svg viewBox="0 0 50 30" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10.4 15.5c0-2.1 1.3-3.1 1.3-3.1-1-1.4-2.4-1.6-3-1.6-1.3-.1-2.4.7-3.1.7-.7 0-1.6-.7-2.7-.7-1.4 0-2.6.8-3.3 2.1-1.5 2.5-.4 6.2 1 8.3.7 1.1 1.5 2.1 2.5 2.1 1 0 1.4-.7 2.7-.7 1.2 0 1.6.7 2.7.7 1.1-.1 1.8-1 2.4-2 .8-1.2 1.1-2.3 1.1-2.3-.1 0-2.1-.8-2.1-3.5z" fill="black" transform="translate(4,0)"/>
                        <path d="M8.4 9.4c.6-.7.9-1.6.8-2.5-.8 0-1.8.6-2.4 1.3-.5.6-.9 1.6-.8 2.5.9.1 1.8-.5 2.4-1.3z" fill="black" transform="translate(4,0)"/>
                        <text x="32" y="18" font-family="Arial, sans-serif" font-size="9" text-anchor="middle" fill="black" font-weight="900">Pay</text>
                      </svg>
                    </div>
                    <!-- Google Pay -->
                    <div class="payment-method-card">
                      <svg viewBox="0 0 50 30" xmlns="http://www.w3.org/2000/svg">
                        <text x="25" y="18" font-family="Arial, sans-serif" font-size="9" text-anchor="middle" fill="black" font-weight="900">G Pay</text>
                      </svg>
                    </div>
                    <!-- Visa -->
                    <div class="payment-method-card">
                      <svg viewBox="0 0 50 30" xmlns="http://www.w3.org/2000/svg">
                        <text x="25" y="19" font-family="Arial, sans-serif" font-size="11" text-anchor="middle" fill="#1A1F71" font-weight="900" style="font-style: italic; letter-spacing: -0.5px;">VISA</text>
                      </svg>
                    </div>
                    <!-- Mastercard -->
                    <div class="payment-method-card">
                      <svg viewBox="0 0 50 30" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="20" cy="12" r="7" fill="#EB001B"/>
                        <circle cx="30" cy="12" r="7" fill="#F79E1B"/>
                        <path d="M25 5.5a7 7 0 0 1 0 13 7 7 0 0 1 0-13z" fill="#FF5F00"/>
                        <text x="25" y="26" font-family="Arial, sans-serif" font-size="3.5" text-anchor="middle" fill="black" font-weight="bold">mastercard</text>
                      </svg>
                    </div>
                  </div>

                  <div class="payment-methods-row">
                    <!-- Maestro -->
                    <div class="payment-method-card">
                      <svg viewBox="0 0 50 30" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="20" cy="12" r="7" fill="#0099CC" opacity="0.8"/>
                        <circle cx="30" cy="12" r="7" fill="#EB001B" opacity="0.8"/>
                        <text x="25" y="26" font-family="Arial, sans-serif" font-size="3.5" text-anchor="middle" fill="black" font-weight="bold">maestro</text>
                      </svg>
                    </div>
                    <!-- American Express -->
                    <div class="payment-method-card">
                      <svg viewBox="0 0 50 30" xmlns="http://www.w3.org/2000/svg">
                        <rect x="8" y="7" width="34" height="16" fill="#007CC3"/>
                        <text x="25" y="18" font-family="Arial, sans-serif" font-size="7.5" text-anchor="middle" fill="white" font-weight="900">AMEX</text>
                      </svg>
                    </div>
                    <!-- PayPal -->
                    <div class="payment-method-card">
                      <svg viewBox="0 0 50 30" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 8h4c2.5 0 3.5 1 3.5 2.5s-2 3.5-4 3.5H8.5l-.8 4h-2L7 8z" fill="#003087"/>
                        <path d="M8.5 9.5h3c2.5 0 3.5 1 3.5 2.5s-1.5 2.5-3.5 2.5H9.5l-.8 4h-2L8.5 9.5z" fill="#0079C1" opacity="0.8"/>
                        <text x="31" y="18.5" font-family="Arial, sans-serif" font-size="8.5" text-anchor="middle" fill="#003087" font-weight="900" style="font-style: italic;">PayPal</text>
                      </svg>
                    </div>
                    <!-- Klarna -->
                    <div class="payment-method-card">
                      <svg viewBox="0 0 50 30" xmlns="http://www.w3.org/2000/svg">
                        <rect x="6" y="9" width="38" height="12" rx="6" fill="#FFB3C7"/>
                        <text x="25" y="17.8" font-family="Arial, sans-serif" font-size="8" text-anchor="middle" fill="black" font-weight="900">Klarna.</text>
                      </svg>
                    </div>
                  </div>
                </div>

                <!-- Trust Features -->
                <div class="trust-features">
                  <div class="trust-feature-item">
                    <div class="feature-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                      </svg>
                    </div>
                    <div class="feature-text">
                      <span class="main-text">{{ t('header.cart.trust.secure_payment') }}</span>
                    </div>
                  </div>

                  <div class="trust-feature-item">
                    <div class="feature-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="1" y="3" width="15" height="13"></rect>
                        <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                        <circle cx="5.5" cy="18.5" r="2.5"></circle>
                        <circle cx="18.5" cy="18.5" r="2.5"></circle>
                      </svg>
                    </div>
                    <div class="feature-text">
                      <span class="main-text">{{ t('header.cart.trust.standard_shipping') }}</span>
                      <span class="sub-text">{{ shipping === 0 ? t('header.cart.trust.free_over', { amount: configStore.freeShippingThreshold }) : t('header.cart.trust.delivery_time') }}</span>
                    </div>
                  </div>

                  <div class="trust-feature-item">
                    <div class="feature-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                      </svg>
                    </div>
                    <div class="feature-text">
                      <span class="main-text">{{ t('header.cart.trust.returns') }}</span>
                      <span class="sub-text">{{ t('header.cart.trust.return_window') }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty Cart -->
        <div v-else class="empty-cart">
          <h2 class="empty-title">{{ t('header.cart.empty') }}</h2>
          <router-link to="/t-shirt" class="btn-shop">
            {{ t('header.cart.back_to_shop') }}
          </router-link>
        </div>
      </div>
    </section>

    <!-- Overlay Guida alle Taglie -->
    <transition name="fade">
      <div v-if="showSizeGuide" class="size-guide-wrapper">
        <div class="size-guide-backdrop" @click="showSizeGuide = false"></div>
        <div class="size-guide-overlay">
          <div class="size-guide-header">
            <div>
              <div class="brand-name">{{ t('brand.name') }}</div>
              <h3 class="size-guide-title">{{ t('product.size_guide.title') }}</h3>
            </div>
            <button type="button" class="btn-close-custom" @click="showSizeGuide = false">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
          </div>
          <div class="size-guide-body">
            <div class="size-guide-model-fit">
              {{ currentLanguage === 'it' ? 'Il modello è alto 188 cm e indossa una taglia L' : 'Model is 188 cm / 6\'2" and is wearing size L' }}
            </div>
            <div v-if="loadingSizeGuide" class="size-guide-loader">
              <div class="spinner-luxury"></div>
            </div>
            <div v-else class="table-responsive">
              <table class="size-guide-table">
                <thead>
                  <tr>
                    <th>{{ t('product.size_guide.table.size') }}</th>
                    <th>{{ t('product.size_guide.table.width') }}</th>
                    <th>{{ t('product.size_guide.table.height') }}</th>
                    <th>{{ t('product.size_guide.table.sleeve') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in sizeGuideData" :key="item.size">
                    <td>{{ item.size }}</td>
                    <td>{{ item.width }}</td>
                    <td>{{ item.height }}</td>
                    <td>{{ item.sleeveLengthCm }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'
import { useCart } from '@/composables/useCart'
import { useLanguage } from '@/composables/useLanguage'
import { useConfigStore } from '@/stores/configStore'
import { useToast } from '@/composables/useToast'
import { useCatalog } from '@/composables/useCatalog'
import { sizeGuideService } from '@/services/sizeGuideService'

const router = useRouter()
const authStore = useAuthStore()
const configStore = useConfigStore()
const toast = useToast()
const { cartItems, cartCount, subtotal, listSubtotal, totalSavings, shipping, total, removeFromCart, updateQuantity, clearCart, isSyncing, fetchServerCart } = useCart()
const { currentLanguage, t } = useLanguage()
const { fetchSizeGuide: apiFetchSizeGuide } = useCatalog()

const showSizeGuide = ref(false)
const loadingSizeGuide = ref(false)
const sizeGuideData = ref([])

const fetchSizeGuide = async () => {
  try {
    loadingSizeGuide.value = true
    const categoryTypeId = cartItems.value.length > 0 ? (cartItems.value[0].categoryTypeId || 1) : 1
    const data = await sizeGuideService.getSizeGuide(categoryTypeId)
    sizeGuideData.value = data || []
  } catch (error) {
    console.error('Error fetching size guide:', error)
    toast.error(t('toast.error.generic'))
  } finally {
    loadingSizeGuide.value = false
  }
}

const handleClearCart = async () => {
  try {
    await clearCart()
  } catch (error) {
    toast.error(t('toast.error.generic'))
  }
}

const handleRemoveFromCart = async (itemId, color, size, variantId, cartItemId) => {
  try {
    await removeFromCart(itemId, color, size, variantId, cartItemId)
  } catch (error) {
    toast.error(t('toast.error.generic'))
  }
}

const handleUpdateQuantity = async (itemId, color, size, quantity, variantId, cartItemId) => {
  try {
    await updateQuantity(itemId, color, size, quantity, variantId, cartItemId)
  } catch (error) {
    toast.error(t('toast.error.generic'))
  }
}

watch(showSizeGuide, (val) => {
  if (val && sizeGuideData.value.length === 0) {
    fetchSizeGuide()
  }
  if (val) {
    document.body.classList.add('modal-open')
  } else {
    document.body.classList.remove('modal-open')
  }
})

onMounted(fetchServerCart)

watch(cartItems, (newVal, oldVal) => {
  if (newVal.length !== oldVal?.length) {
    fetchServerCart()
  }
}, { deep: true })

const getImagePath = (imageName) => {
  if (!imageName) return ''
  if (imageName.startsWith('http') || imageName.startsWith('//')) return imageName
  if (imageName.startsWith('/')) return imageName
  if (imageName.includes('/')) return `/${imageName}`
  return `/images/products/${imageName}`
}

const formatPrice = (price) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(price)
}

const goToCheckout = () => {
  router.push('/checkout')
}
</script>

<style scoped>
.cart-page {
  background: #fff;
  overflow-x: hidden;
  overflow-y: visible;
}

.cart-content {
  padding: 80px 0;
  min-height: 60vh;
}

.cart-wrapper {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 60px;
  align-items: start; /* Fix per position: sticky */
  min-width: 0;
}

.cart-wrapper > * {
  min-width: 0;
}

@media (max-width: 1200px) {
  .cart-wrapper {
    gap: 30px;
    grid-template-columns: 1fr 320px;
  }
}

@media (max-width: 991px) {
  .cart-wrapper {
    grid-template-columns: minmax(0, 1fr);
    gap: 40px;
    min-width: 0;
  }
  
  .cart-content {
    padding: 40px 0;
  }
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  border-bottom: 1px solid #eaeaea;
  padding-bottom: 20px;
}

.section-title {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #000;
  margin-bottom: 0;
  border-bottom: none;
  padding-bottom: 0;
}

.btn-clear-cart {
  background: transparent;
  border: none;
  color: #ccc; /* Grigio più tenue */
  font-size: 10px; /* Ridotto da 11px */
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: color 0.2s;
  padding: 5px 10px;
  text-decoration: none;
}

.btn-clear-cart:hover:not(:disabled) {
  color: #888; /* Meno contrastato al passaggio */
}

.btn-clear-cart:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.cart-items {
  display: flex;
  flex-direction: column;
}

.cart-item {
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr) auto;
  grid-template-areas: "image details actions";
  align-items: start;
  column-gap: 30px;
  padding: 50px 0;
  border-bottom: 1px solid #f0f0f0;
}

@media (max-width: 1400px) {
  .cart-item {
    grid-template-columns: 280px minmax(0, 1fr) auto;
  }
}

@media (max-width: 1200px) {
  .cart-item {
    grid-template-columns: 200px minmax(0, 1fr) auto;
    column-gap: 20px;
    padding: 40px 0;
  }
}

@media (max-width: 991px) {
  .cart-item {
    grid-template-columns: 140px minmax(0, 1fr);
    grid-template-areas:
      "image details"
      "actions actions";
    column-gap: 15px;
    row-gap: 15px;
    padding: 30px 0;
  }

  .item-details {
    min-width: 0;
  }

  .item-premium-info {
    max-width: none;
    width: 100%;
  }
}

.item-image {
  grid-area: image;
  width: 100%;
  aspect-ratio: 3/4;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 4px;
}

.cart-item-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  image-rendering: auto;
  transform: translateZ(0);
}

.item-content-wrapper {
  display: contents; 
}

.item-main-info {
  display: contents;
}

.item-details {
  grid-area: details;
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding-left: 10px;
  align-self: start;
}

.item-footer-actions {
  grid-area: actions;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-start;
  gap: 12px;
  height: auto;
}

@media (max-width: 991px) {
  .item-footer-actions {
    align-items: flex-start;
  }
}

.item-name {
  font-size: 16px;
  font-weight: 700;
  color: #000;
  text-transform: uppercase;
  margin-bottom: 0;
  letter-spacing: 1.2px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-header-row {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  gap: 15px;
}

.item-info {
  font-size: 11px;
  color: #999;
  text-transform: uppercase;
  margin-bottom: 4px;
  letter-spacing: 0.5px;
}

.item-sku {
  font-size: 9px;
  color: #888;
  background-color: #f9f9f9;
  padding: 2px 8px;
  border-radius: 2px;
  letter-spacing: 0.5px;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 12px;
  width: fit-content;
  font-family: 'Courier New', monospace;
}

.item-price-wrapper {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.item-price {
  font-size: 15px;
  font-weight: 600;
  color: #000;
}

.item-old-price {
  font-size: 12px;
  color: #bbb;
  text-decoration: line-through;
  font-weight: 400;
}

.item-discount-badge {
  font-size: 11px;
  color: #d9534f;
  font-weight: 700;
}

.quantity-selector {
  display: flex;
  align-items: center;
  background-color: #f8f8f8;
  border-radius: 2px;
  overflow: hidden;
  border: none;
}

.qty-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  font-size: 16px;
  color: #111;
  cursor: pointer;
  transition: background-color 0.2s;
}

.qty-btn:hover {
  background-color: #efefef;
}

.qty-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.qty-value {
  width: 30px;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  color: #111;
}

.item-total {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  text-align: right;
  min-width: 100px;
}

.total-price {
  font-size: 16px;
  font-weight: 700;
  color: #000;
  margin: 0;
  line-height: 1.2;
}

.price-vat-label-item {
  font-size: 9px;
  color: #999;
  font-weight: 400;
  letter-spacing: 0.5px;
  margin-top: 4px;
  text-transform: none;
}

@media (max-width: 991px) {
  .item-total {
    justify-content: flex-start;
  }
}

.btn-remove {
  background: transparent;
  border: none;
  color: #ccc;
  cursor: pointer;
  padding: 0;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.btn-remove:hover {
  color: #000;
  transform: scale(1.1);
}

/* Order Summary */
.order-summary {
  height: fit-content;
  position: sticky;
  top: 130px;
}

@media (max-width: 991px) {
  .order-summary {
    position: static;
    margin-top: 20px;
  }
}

.summary-title {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: #000;
  margin-bottom: 30px;
}

.summary-content {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.summary-row .label {
  font-size: 11px;
  font-weight: 700;
  color: #000;
  letter-spacing: 1px;
}

.summary-row .value {
  font-size: 13px;
  font-weight: 500;
  color: #000;
}

.summary-row.discount-row .label,
.summary-row.discount-row .value {
  color: #d9534f;
}

.subtotal-row-main {
  padding-top: 15px;
  border-top: 1px solid #eaeaea;
  margin-top: 10px;
}

.subtotal-row-main .label {
  text-transform: uppercase;
  letter-spacing: 1.5px;
}

.subtotal-row-main .value {
  font-weight: 700;
  font-size: 15px;
}

.value-wrapper {
  text-align: right;
}

.sub-text {
  font-size: 9px;
  color: #666; /* Scurito da #888 per migliore contrasto */
}

.free-shipping {
  color: #28a745;
  font-weight: 700;
}

.summary-divider {
  height: 1px;
  background-color: #000;
  margin: 15px 0;
}

.total-row .label {
  font-size: 14px;
  font-weight: 900;
  letter-spacing: 2px;
}

.total-row .value {
  font-size: 18px;
  font-weight: 900;
}

.total-value-container {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.price-vat-label-cart {
  font-size: 10px;
  color: #888;
  font-weight: 400;
  margin-top: 2px;
  letter-spacing: 0.5px;
  text-transform: none;
}

.price-summary-details-extra {
  padding: 0 0 15px 0;
  border-bottom: 1px solid #f2f2f2;
  margin-bottom: 20px;
}

.summary-row.small-info {
  margin-bottom: 0;
}

.summary-row.small-info .label {
  font-size: 9px;
  font-weight: 400;
  color: #999;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.premium-info-box {
  background-color: #fafafa;
  border: 1px solid #f0f0f0;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.info-box-item {
  display: flex;
  align-items: center;
  gap: 15px;
}

.info-box-item.clickable {
  cursor: pointer;
  transition: opacity 0.2s;
}

.info-box-item.clickable:hover {
  opacity: 0.7;
}

.info-box-icon {
  width: 32px;
  height: 32px;
  background-color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #111;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  flex-shrink: 0;
}

.info-box-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.info-box-title {
  font-size: 11px;
  font-weight: 700;
  color: #000;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-box-desc {
  font-size: 10px;
  color: #666;
}

.info-box-link {
  font-size: 10px;
  color: #000;
  text-decoration: underline;
  text-underline-offset: 3px;
  font-weight: 500;
}

.info-box-divider {
  height: 1px;
  background-color: #eee;
  width: 100%;
}

/* Size Guide Overlay (Reused from Product Modal) */
.size-guide-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 11000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.size-guide-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0,0,0,0.6);
  backdrop-filter: blur(4px);
}

.size-guide-overlay {
  position: relative;
  background: #fff;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 40px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

@media (max-width: 576px) {
  .size-guide-overlay {
    padding: 25px;
  }
}

.size-guide-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;
}

.size-guide-title {
  font-size: 24px;
  font-weight: 800;
  color: #111;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: 5px;
}

.size-guide-model-fit {
  background: #f9f9f9;
  padding: 12px 15px;
  font-size: 12px;
  color: #666;
  margin-bottom: 25px;
  border-left: 3px solid #111;
}

.size-guide-table {
  width: 100%;
  border-collapse: collapse;
}

.size-guide-table th {
  padding: 12px 10px;
  text-align: left;
  font-size: 10px;
  font-weight: 700;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-bottom: 1px solid #eee;
}

.size-guide-table td {
  padding: 15px 10px;
  font-size: 13px;
  color: #111;
  border-bottom: 1px solid #f5f5f5;
}

.size-guide-table tr:last-child td {
  border-bottom: none;
}

.spinner-luxury {
  width: 40px;
  height: 40px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #111;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 40px auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.btn-close-custom {
  background: none;
  border: none;
  color: #111;
  cursor: pointer;
  padding: 5px;
  transition: transform 0.2s;
}

.btn-close-custom:hover {
  transform: scale(1.1);
}

.brand-name {
  font-size: 10px;
  font-weight: 600;
  color: #999;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.btn-checkout {
  width: 100%;
  background-color: #000;
  color: #fff;
  border: none;
  padding: 18px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  cursor: pointer;
  margin-top: 20px;
  transition: opacity 0.2s;
}

.btn-checkout:hover:not(:disabled) {
  opacity: 0.8;
}

.btn-checkout:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.payment-trust-info {
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.trust-divider {
  width: 100%;
  height: 1px;
  background-color: #f2f2f2;
  margin-bottom: 25px;
}

.payment-methods {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 30px;
  width: 100%;
}

.payment-methods-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
}

.payment-method-card {
  width: 75px;
  height: 45px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 2px;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
  flex-shrink: 0;
}

.payment-method-card:hover {
  border-color: #000;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.trust-features {
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 15px;
}

.trust-feature-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  text-decoration: none;
  color: #000;
}

.feature-icon {
  margin-bottom: 12px;
}

.feature-text {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-height: 36px;
  justify-content: flex-start;
  width: 100%;
}

.main-text {
  display: block;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.3;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.sub-text {
  display: block;
  font-size: 9px;
  color: #666;
  font-weight: 400;
  line-height: 1.3;
}

.empty-cart {
  text-align: center;
  padding: 100px 0;
}

.empty-title {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 2px;
  margin-bottom: 30px;
}

.btn-shop {
  display: inline-block;
  background-color: #000;
  color: #fff;
  padding: 15px 40px;
  text-decoration: none;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
}

.item-sku-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
  margin-top: 4px;
}

.sku-label-text {
  font-size: 8px;
  color: #bbb;
  font-weight: 500;
  letter-spacing: 1px;
}

.sku-value {
  font-size: 9px;
  color: #777;
  background-color: #f8f8f8;
  padding: 1px 6px;
  border-radius: 1px;
  letter-spacing: 0.5px;
  font-weight: 600;
  text-transform: uppercase;
  font-family: 'Courier New', monospace;
  border-left: 1px solid #ddd;
}

.price-vat-label-item {
  font-size: 9px;
  color: #999;
  font-weight: 400;
  letter-spacing: 0.5px;
  margin-top: 2px;
}

/* Integrated Per-Item Info Boxes */
.item-premium-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 15px;
  background-color: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 2px;
  max-width: 320px;
}

.item-info-box {
  display: flex;
  align-items: center;
  gap: 12px;
}

.item-info-box.clickable {
  cursor: pointer;
  transition: opacity 0.2s;
}

.item-info-box.clickable:hover {
  opacity: 0.7;
}

.info-box-icon-small {
  width: 26px;
  height: 26px;
  background-color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #111;
  box-shadow: 0 2px 6px rgba(0,0,0,0.03);
  flex-shrink: 0;
}

.info-box-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.info-box-title-small {
  font-size: 10px;
  font-weight: 700;
  color: #000;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-box-desc-small {
  font-size: 9px;
  color: #666;
}

.info-box-link-small {
  font-size: 9px;
  color: #000;
  text-decoration: underline;
  text-underline-offset: 2px;
  font-weight: 500;
}

/* ===================== RESPONSIVE MOBILE-PORTRAIT ===================== */

@media (max-width: 768px) {
  .cart-content {
    padding: 32px 0;
  }

  .cart-item {
    grid-template-columns: 120px minmax(0, 1fr);
    column-gap: 14px;
    row-gap: 12px;
    padding: 24px 0;
  }

  .item-details {
    gap: 12px;
  }

  .item-header-row {
    gap: 12px;
  }

  .item-name {
    font-size: 15px;
  }

  .section-header {
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 24px;
    padding-bottom: 16px;
  }

  .section-title {
    margin: 0;
  }

  .summary-title {
    margin-bottom: 20px;
  }

  .summary-content {
    gap: 12px;
  }

  .payment-method-card {
    width: 64px;
    height: 38px;
    padding: 5px;
  }

  .trust-features {
    gap: 12px;
  }

  .feature-text {
    min-height: 34px;
  }

  .btn-checkout {
    padding: 16px;
    font-size: 11px;
  }

  .summary-divider {
    margin: 12px 0;
  }
}

@media (max-width: 576px) {
  .cart-content {
    padding: 24px 0;
  }

  .cart-page .cart-content {
    min-height: auto;
  }

  .cart-item {
    grid-template-columns: 110px minmax(0, 1fr);
    column-gap: 12px;
    row-gap: 10px;
    padding: 20px 0;
  }

  .item-premium-info {
    padding: 12px;
  }

  .item-details {
    gap: 10px;
  }

  .item-header-row {
    gap: 10px;
  }

  .item-name {
    font-size: 14px;
    letter-spacing: 1px;
  }

  .item-info {
    font-size: 10px;
  }

  .item-price {
    font-size: 14px;
  }

  .item-total {
    min-width: 80px;
  }

  .qty-btn {
    width: 28px;
    height: 28px;
    font-size: 14px;
  }

  .qty-value {
    width: 26px;
    font-size: 12px;
  }

  .price-vat-label-item,
  .price-vat-label-cart {
    font-size: 8px;
  }

  .section-header {
    gap: 10px;
    margin-bottom: 20px;
    padding-bottom: 14px;
  }

  .section-title {
    font-size: 12px;
  }

  .btn-clear-cart {
    font-size: 9px;
    padding: 4px 8px;
  }

  .summary-title {
    font-size: 12px;
    margin-bottom: 18px;
  }

  .summary-content {
    gap: 10px;
  }

  .subtotal-row-main .label,
  .total-row .label {
    font-size: 12px;
  }

  .total-row .value {
    font-size: 16px;
  }

  .total-value-container {
    align-items: flex-end;
  }

  .payment-method-card {
    width: 60px;
    height: 36px;
    padding: 4px;
  }

  .payment-methods {
    margin-bottom: 20px;
  }

  .trust-features {
    gap: 10px;
  }

  .feature-text {
    min-height: 30px;
  }

  .main-text {
    font-size: 9px;
  }

  .sub-text {
    font-size: 8px;
  }

  .btn-checkout {
    padding: 15px;
    font-size: 10px;
    margin-top: 16px;
  }

  .summary-divider {
    margin: 10px 0;
  }
}

@media (max-width: 430px) {
  .cart-content {
    padding: 16px 0;
  }

  .cart-item {
    grid-template-columns: 90px minmax(0, 1fr);
    column-gap: 10px;
    padding: 16px 0;
  }

  .item-premium-info {
    padding: 10px;
  }

  .item-header-row {
    gap: 8px;
  }

  .item-name {
    font-size: 13px;
  }

  .item-price {
    font-size: 13px;
  }

  .item-total {
    min-width: 70px;
  }

  .qty-btn {
    width: 26px;
    height: 26px;
    font-size: 13px;
  }

  .qty-value {
    width: 24px;
    font-size: 11px;
  }

  .section-header {
    gap: 8px;
    margin-bottom: 16px;
    padding-bottom: 12px;
  }

  .section-title {
    font-size: 11px;
  }

  .btn-clear-cart {
    font-size: 8px;
    padding: 3px 6px;
  }

  .summary-title {
    font-size: 11px;
    margin-bottom: 16px;
  }

  .summary-content {
    gap: 8px;
  }

  .summary-row .label {
    font-size: 9px;
  }

  .summary-row .value {
    font-size: 11px;
  }

  .subtotal-row-main .value {
    font-size: 12px;
  }

  .total-row .label {
    font-size: 11px;
  }

  .total-row .value {
    font-size: 14px;
  }

  .price-vat-label-cart,
  .price-vat-label-item {
    font-size: 7px;
  }

  .payment-method-card {
    width: 54px;
    height: 32px;
    padding: 3px;
  }

  .trust-features {
    gap: 8px;
  }

  .feature-text {
    min-height: 28px;
  }

  .main-text {
    font-size: 8px;
  }

  .sub-text {
    font-size: 7px;
  }

  .btn-checkout {
    padding: 13px;
    font-size: 9px;
    margin-top: 14px;
  }

  .summary-divider {
    margin: 8px 0;
  }

  .empty-cart {
    padding: 60px 0;
  }

  .empty-title {
    font-size: 16px;
  }

  .btn-shop {
    padding: 12px 32px;
    font-size: 10px;
  }
}

/* Portrait orientation safety for very short viewports */
@media (max-width: 576px) and (orientation: portrait) {
  .cart-content {
    padding: 20px 0;
  }
}
</style>
